import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, PLATFORM_ID } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { Observable, catchError, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { CmsContent } from '@fcom/core-api';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { isDeepEqual } from '@fcom/core/utils/utils';
import { finShare } from '@fcom/rx';
import {
  DestinationTimetableInfo,
  PriceCalendarService,
} from '@fcom/common/services/price-calendar/price-calendar.service';
import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { PriceCalendarWithPricesParams } from '@fcom/common/interfaces/price-calendar.interface';
import * as GlobalBookingActions from '@fcom/common/store/actions/global-booking.actions';
import { DirectFlightFrequency } from '@fcom/common/interfaces/timetable-search.interface';
import {
  selectGlobalBookingPaxAmount,
  selectGlobalBookingTravelClass,
  selectGlobalBookingTripType,
} from '@fcom/common/store/selectors/global-booking.selectors';
import { OndMarketingOffersService } from '@fcom/ond-marketing-landing-core/services/ond-marketing-offers.service';
import { transformLocationCode } from '@fcom/ond-marketing-landing-core/services/utils';
import { OndLocationsResponse } from '@fcom/ond-marketing-landing-core/interfaces/ond-marketing-offers.interface';
import { LoaderTheme } from '@fcom/ui-components/components/loader/enums';

@Component({
  selector: 'cms-destination-flight-info',
  templateUrl: 'cms-destination-flight-info.component.html',
  styleUrls: ['cms-destination-flight-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsDestinationFlightInfoComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private priceCalendarService = inject(PriceCalendarService);
  private ondMarketingOffersService = inject(OndMarketingOffersService);
  private store = inject(Store);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly pricesAvailable = input<boolean | null>(null);
  readonly initialTripType = input<TripType | null>(null);

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TripType = TripType;
  readonly LoaderTheme = LoaderTheme;

  private readonly route$: Observable<{ origin: string; destination: string }> = this.route.params.pipe(
    map((params) => ({
      origin: transformLocationCode(params['originLocationCode']),
      destination: transformLocationCode(params['destinationLocationCode']),
    })),
    distinctUntilChanged((prev, curr) => {
      return prev.origin === curr.origin && prev.destination === curr.destination;
    }),
    finShare()
  );

  private readonly locationData$: Observable<OndLocationsResponse> = this.route$.pipe(
    switchMap(({ origin, destination }) =>
      destination
        ? this.ondMarketingOffersService
            .getLocationData([origin, destination])
            .pipe(catchError(() => of({} as OndLocationsResponse)))
        : of({} as OndLocationsResponse)
    ),
    finShare()
  );

  private readonly tripType = this.store.selectSignal(selectGlobalBookingTripType);
  private readonly travelClass = this.store.selectSignal(selectGlobalBookingTravelClass);
  private readonly paxAmount = this.store.selectSignal(selectGlobalBookingPaxAmount);
  private readonly routeLocations = toSignal(this.route$);

  private readonly priceParams = computed<PriceCalendarWithPricesParams>(() => {
    return {
      origin: this.routeLocations()?.origin,
      destination: this.routeLocations()?.destination,
      tripType: this.tripType(),
      travelClass: this.travelClass(),
      paxAmount: this.paxAmount(),
    };
  });

  private readonly timetableInfo$: Observable<DestinationTimetableInfo> = this.route$.pipe(
    switchMap(({ origin, destination }) => {
      return this.priceCalendarService.getDestinationTimetableInfo(origin, destination);
    }),
    finShare()
  );

  private readonly startingFromPrice$: Observable<{ price?: Amount } | undefined> = this.isBrowser
    ? toObservable(this.priceParams).pipe(
        distinctUntilChanged(isDeepEqual),
        switchMap((params) => {
          return this.priceCalendarService.getStartingFromPrice(params).pipe(
            map((price) => {
              return { price };
            })
          );
        }),
        finShare()
      )
    : of(undefined);

  private readonly destination = toSignal(this.route$.pipe(map(({ destination }) => destination)), {
    initialValue: '',
  });
  private readonly locationData = toSignal(this.locationData$);
  private readonly timetableInfo = toSignal(this.timetableInfo$);
  private readonly startingFromPriceResult = toSignal(this.startingFromPrice$);

  readonly content = input<CmsContent>();

  readonly cityName = computed<string | undefined>(() => this.locationData()?.[this.destination()]?.cityName);
  readonly picture = computed(() => {
    return this.content()?.picture ?? this.locationData()?.[this.destination()]?.picture ?? undefined;
  });

  readonly startingFromPrice = computed<Amount | undefined>(() => {
    return this.startingFromPriceResult()?.price;
  });
  readonly isPriceLoading = computed<boolean>(() => {
    return this.pricesAvailable() === true && this.startingFromPriceResult() === undefined;
  });
  readonly showPriceColumn = computed<boolean>(() => {
    return this.isPriceLoading() || !!this.startingFromPrice();
  });
  readonly isLoading = computed<boolean>(() => {
    return this.timetableInfo() === undefined && !this.showPriceColumn();
  });
  readonly duration = computed(() => {
    return this.timetableInfo()?.duration;
  });
  readonly airportCodes = computed<string[]>(() => {
    return this.timetableInfo()?.airportCodes ?? [];
  });
  readonly isDirect = computed<boolean>(() => {
    return this.timetableInfo()?.isDirect ?? false;
  });
  readonly directFrequency = computed<DirectFlightFrequency | undefined>(() => {
    return this.timetableInfo()?.directFrequency;
  });
  readonly directFrequencyKey = computed<string>(() => {
    const frequency = this.directFrequency();
    const unit = frequency?.unit === 'day' ? 'perDay' : 'perWeek';
    return `ondMarketing.destinationFlightInfo.frequency.${unit}.${frequency?.count === 1 ? 'singular' : 'plural'}`;
  });
  readonly directFrequencyUnitKey = computed<string>(() => {
    return `ondMarketing.destinationFlightInfo.frequency.unit.${this.directFrequency()?.unit === 'day' ? 'day' : 'week'}`;
  });
  readonly showCard = computed<boolean>(() => {
    return this.isLoading() || this.showPriceColumn() || this.airportCodes().length > 0 || this.isDirect();
  });
  readonly isRoundTrip = computed<boolean>(() => this.tripType() === TripType.RETURN);

  ngOnInit(): void {
    const initialTripType = this.initialTripType();
    if (initialTripType && initialTripType !== this.tripType()) {
      this.store.dispatch(GlobalBookingActions.setTripType({ tripType: initialTripType }));
    }
  }
}
